import React from 'react';
import { useTranslation } from 'react-i18next';
import { VMConfig, OperatingSystemOption } from '../../../types/vm.type';
import OSCard from '../components/OSCard';
import { osTypeFilters } from '../../../constants/vmConstants';
import { useSettings } from '../../../contexts/SettingsContext';

interface Step1VMProps {
  config: VMConfig;
  setConfig: (config: VMConfig | ((prev: VMConfig) => VMConfig)) => void;
  operatingSystems: OperatingSystemOption[];
  selectedOSType: string | null;
  setSelectedOSType: (value: string | null) => void;
  availableOSTypes: string[];
  isLoading: boolean;
}

const Step1VM: React.FC<Step1VMProps> = ({
  config,
  setConfig,
  operatingSystems,
  selectedOSType,
  setSelectedOSType,
  isLoading,
}) => {
  const { t } = useTranslation();
  const { isVmProductVisible } = useSettings();

  // El admin oculta SO/productos VM concretos (por id de producto del catálogo).
  // missing = visible. Las pestañas de tipo se derivan de lo que quedó visible.
  const visibleOperatingSystems = operatingSystems.filter((os) => isVmProductVisible(os.id));
  const visibleOSTypes = Array.from(
    new Set(
      visibleOperatingSystems
        .map((os) => (os.osType || '').toLowerCase())
        .filter((t): t is string => Boolean(t))
    )
  );

  return (
    <div className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('virtualMachines.create.step1.name')} *
        </label>
        <input
          type="text"
          value={config.name}
          onChange={(e) => setConfig((prev) => ({ ...prev, name: e.target.value }))}
          placeholder={t('virtualMachines.create.step1.namePlaceholder')}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-black dark:focus:ring-white focus:border-transparent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t('virtualMachines.create.step1.namePlaceholder')}
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          {t('virtualMachines.create.step1.os')} *
        </label>

        {visibleOSTypes.length > 0 && (
          <div className="mb-4">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setSelectedOSType(null)}
                className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                  selectedOSType === null
                    ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                }`}
              >
                <span>All</span>
              </button>
              {osTypeFilters
                .filter((filter) => visibleOSTypes.includes(filter.id))
                .map((filter) => (
                  <button
                    key={filter.id}
                    onClick={() => setSelectedOSType(filter.id)}
                    className={`px-4 py-2 rounded-lg border-2 transition-all flex items-center gap-2 ${
                      selectedOSType === filter.id
                        ? 'border-black dark:border-white bg-gray-100 dark:bg-gray-700 font-medium text-gray-900 dark:text-white'
                        : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 text-gray-700 dark:text-gray-300'
                    }`}
                  >
                    {filter.isImage ? (
                      <img src={filter.icon} alt={filter.name} className="h-5 object-contain" />
                    ) : (
                      <span className="text-xl">{filter.icon}</span>
                    )}
                    <span>{filter.name}</span>
                  </button>
                ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>{t('common.loading')}</p>
          </div>
        ) : visibleOperatingSystems.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p>{t('virtualMachines.create.step1.selectOS')}</p>
            {selectedOSType && (
              <button
                onClick={() => setSelectedOSType(null)}
                className="mt-4 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 underline"
              >
                {t('common.filter')}
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3">
              {visibleOperatingSystems.map((os) => (
                <OSCard
                  key={os.id}
                  os={os}
                  isSelected={config.os === os.id}
                  onSelect={() => setConfig((prev) => ({ ...prev, os: os.id }))}
                />
              ))}
            </div>
            {config.os && (() => {
              const selectedOS = operatingSystems.find((os) => os.id === config.os);
              return selectedOS?.description ? (
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <span className="font-semibold text-gray-900 dark:text-white">{selectedOS.name}:</span>{' '}
                    {selectedOS.description}
                  </p>
                </div>
              ) : null;
            })()}
          </>
        )}
      </div>
    </div>
  );
};

export default Step1VM;
